import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const W = 300, BASE_BLOCK = 100, H = 360, ROW_H = 28;

export function TowerStack({ onScore }) {
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const [stack, setStack] = useState([]); // {x, w}
  const [block, setBlock] = useState({ x: 0, w: BASE_BLOCK });
  const dir = useRef(1);
  const raf = useRef(null);
  const speed = useRef(2.2);
  const reported = useRef(false);

  const start = () => {
    setStack([{ x: (W - BASE_BLOCK) / 2, w: BASE_BLOCK }]);
    setBlock({ x: 0, w: BASE_BLOCK }); dir.current = 1; speed.current = 2.2;
    setScore(0); setOver(false); setRunning(true); reported.current = false;
  };

  useEffect(() => {
    if (!running) return;
    const loop = () => {
      setBlock((b) => {
        let nx = b.x + dir.current * speed.current;
        if (nx <= 0) { nx = 0; dir.current = 1; }
        if (nx + b.w >= W) { nx = W - b.w; dir.current = -1; }
        return { ...b, x: nx };
      });
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const drop = useCallback(() => {
    if (!running) return;
    setStack((st) => {
      const top = st[st.length - 1];
      const left = Math.max(block.x, top.x);
      const right = Math.min(block.x + block.w, top.x + top.w);
      const overlap = right - left;
      if (overlap <= 0) { setRunning(false); setOver(true); return st; }
      const newBlock = { x: left, w: overlap };
      speed.current = Math.min(6, speed.current + 0.15);
      setScore((s) => s + 1);
      setBlock({ x: 0, w: overlap });
      return [...st, newBlock];
    });
  }, [running, block]);

  useEffect(() => {
    const key = (e) => { if (e.code === "Space") { e.preventDefault(); drop(); } };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [drop]);

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 80); } }, [over, score, onScore]);

  const visibleStack = stack.slice(-Math.floor(H / ROW_H));

  return (
    <div className="flex flex-col items-center" data-testid="tower-stack">
      <p className="font-mono text-neon-cyan text-lg mb-3" data-testid="tower-score">HEIGHT: {score}</p>
      <div onClick={drop} className="relative bg-zinc-950 border-2 border-zinc-700 overflow-hidden cursor-pointer" style={{ width: W, maxWidth: "92vw", height: H }}>
        {visibleStack.map((b, i) => (
          <div key={i} className="absolute rounded-sm" style={{ left: b.x, bottom: i * ROW_H, width: b.w, height: ROW_H - 3, background: `hsl(${180 + i * 18}, 90%, 55%)` }} />
        ))}
        {running && (
          <div className="absolute rounded-sm" style={{ left: block.x, bottom: visibleStack.length * ROW_H, width: block.w, height: ROW_H - 3, background: "#fff", boxShadow: "0 0 10px #fff" }} />
        )}
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="tower-over">Toppled! Height {score}</p>}
            <Button onClick={(e) => { e.stopPropagation(); start(); }} data-testid="tower-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Tap / SPACE to drop the block. Stack it as high as you can!</p>
    </div>
  );
}
